<template>
  <div class="help-button-wrapper" style="position: relative;">
    <!-- "?" trigger -->
    <div
      class="cursor-pointer flex items-center sm:mr-4 mr-2"
      @click="togglePanel"
      title="Help & Feedback"
    >
      <span class="help-trigger-btn">?</span>
    </div>

    <!-- Overlay to close panel on outside click -->
    <div
      v-if="showPanel"
      @click="showPanel = false"
      style="position: fixed; inset: 0; z-index: 99998;"
    ></div>

    <!-- Floating panel -->
    <transition name="help-slide">
      <div v-if="showPanel" class="help-panel">

        <!-- Header bar -->
        <div class="help-panel-header">
          <div style="display: flex; align-items: center; gap: 8px;">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <circle cx="12" cy="12" r="10"/><path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/><line x1="12" y1="17" x2="12.01" y2="17"/>
            </svg>
            <span>Help &amp; Feedback</span>
          </div>
          <span @click="showPanel = false" class="help-close-btn">&times;</span>
        </div>

        <!-- Body -->
        <div class="help-panel-body">

          <!-- Title -->
          <div class="help-field">
            <label class="help-label">Subject</label>
            <input
              v-model="taskTitle"
              type="text"
              class="help-input"
              placeholder="e.g. Bug on invoice page"
            />
          </div>

          <!-- Message -->
          <div class="help-field">
            <label class="help-label">Description</label>
            <textarea
              v-model="taskMessage"
              class="help-input help-textarea"
              placeholder="Describe the issue or your suggestion..."
              rows="4"
            ></textarea>
          </div>

          <!-- Attachments row -->
          <div style="display: flex; gap: 10px;">
            <!-- File -->
            <div class="help-field" style="flex: 1;">
              <label class="help-label">Attachment</label>
              <label class="help-file-label">
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
                  <path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48"/>
                </svg>
                <span>{{ attachedFilename || 'Choose file' }}</span>
                <input type="file" @change="onFileChange" style="display: none;" />
              </label>
            </div>

            <!-- Screenshot -->
            <div class="help-field" style="flex: 1;">
              <label class="help-label">Screenshot</label>
              <button
                @click="captureScreenshot"
                :disabled="capturing"
                class="help-capture-btn"
                :class="{ 'has-screenshot': screenshotBase64, 'is-capturing': capturing }"
              >
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" :class="{ 'help-spinner': capturing }">
                  <rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><polyline points="21 15 16 10 5 21"/>
                </svg>
                <span>{{ capturing ? 'Wait…' : screenshotBase64 ? 'Retake' : 'Capture' }}</span>
              </button>
              <label class="help-upload-link">
                or upload
                <input type="file" accept="image/*" @change="onScreenshotFileChange" style="display: none;" />
              </label>
              <span v-if="screenshotBase64" @click="screenshotBase64 = ''" class="help-remove-link">Remove</span>
            </div>
          </div>

          <!-- Screenshot thumbnail -->
          <div v-if="screenshotBase64" style="margin-top: 4px;">
            <img :src="screenshotBase64" alt="screenshot" class="help-screenshot-thumb" />
          </div>

        </div>

        <!-- Footer -->
        <div class="help-panel-footer">
          <div style="display: flex; gap: 8px; width: 100%; flex-direction: column;">
            <div style="display: flex; align-items: center; justify-content: space-between;">
              <span style="font-size: 11px; color: #aaa;">Sent to contact@codesinn.com</span>
              <button
                @click="sendHelp"
                :disabled="sending || !taskTitle.trim()"
                class="help-send-btn"
                :class="{ 'help-send-btn--disabled': sending || !taskTitle.trim() }"
              >
                <span v-if="!sending">Send</span>
                <span v-else style="display:flex;align-items:center;gap:5px;">
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" class="help-spinner"><circle cx="12" cy="12" r="10" stroke-dasharray="40" stroke-dashoffset="10"/></svg>
                  Sending…
                </span>
              </button>
            </div>
            <div v-if="isAdmin" style="border-top: 2px dashed #e2e8f0; padding-top: 10px; margin-top: 2px;">
              <button
                @click="sendTodoRequest"
                :disabled="sendingTodo"
                class="help-todo-btn"
                :class="{ 'help-todo-btn--disabled': sendingTodo }"
              >
                <span v-if="!sendingTodo">Check my to-do list</span>
                <span v-else style="display:flex;align-items:center;gap:5px;">
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" class="help-spinner"><circle cx="12" cy="12" r="10" stroke-dasharray="40" stroke-dashoffset="10"/></svg>
                  Sending…
                </span>
              </button>
            </div>
          </div>
        </div>

      </div>
    </transition>
  </div>
</template>

<script>
import axios from '@/axios.js'
import html2canvas from 'html2canvas'

export default {
  name: 'HelpButton',
  data() {
    return {
      showPanel: false,
      taskTitle: '',
      taskMessage: '',
      attachedFile: '',
      attachedFilename: '',
      screenshotBase64: '',
      screenshotFilename: '',
      sending: false,
      capturing: false,
      sendingTodo: false,
    }
  },
  computed: {
    isAdmin() {
      return this.$store.state.AppActiveUser.userRole === 'admin'
    }
  },
  methods: {
    togglePanel() {
      this.showPanel = !this.showPanel
    },

    onFileChange(e) {
      const file = e.target.files[0]
      if (!file) return
      this.attachedFilename = file.name
      const reader = new FileReader()
      reader.onload = (ev) => { this.attachedFile = ev.target.result }
      reader.readAsDataURL(file)
    },

    onScreenshotFileChange(e) {
      const file = e.target.files[0]
      if (!file) return
      this.screenshotFilename = file.name
      const reader = new FileReader()
      reader.onload = (ev) => { this.screenshotBase64 = ev.target.result }
      reader.readAsDataURL(file)
    },

    async captureScreenshot() {
      this.capturing = true
      this.showPanel = false
      await this.$nextTick()
      await new Promise(r => setTimeout(r, 250))
      try {
        const canvas = await html2canvas(document.body, { useCORS: true, allowTaint: true, scale: 1 })
        this.screenshotBase64 = canvas.toDataURL('image/png')
        this.screenshotFilename = `screenshot-${Date.now()}.png`
      } catch (e) {
        console.error('Screenshot failed:', e)
      }
      this.showPanel = true
      this.capturing = false
    },

    sendHelp() {
      if (!this.taskTitle.trim() || this.sending) return
      this.sending = true
      const userEmail = localStorage.getItem('login-email') || 'unknown@xylo.app'

      const dataz = {
        template_id: 'template_y3bi84v',
        template_params: {
          se_title: this.taskTitle,
          se_hello: this.taskMessage,
          se_from_email: 'sales@acaciabois.com',
          se_reply_mail: userEmail,
          se_message: null,
          se_goodbye: 'Sent from XYLO APP',
          se_mail: 'contact@codesinn.com',
          se_from_name: userEmail,
          attachment_content: this.attachedFile || '',
          filename: this.attachedFilename || '',
          attachment_content2: this.screenshotBase64 || '',
          filename2: this.screenshotFilename || ''
        }
      }

      axios
        .post('/mail/send', dataz)
        .then(() => {
          this.$vs.notify({ color: 'success', title: 'Message sent!', text: 'Support will get back to you shortly.' })
          this.showPanel = false
          this.resetForm()
        })
        .catch((error) => {
          const errMsg = (error.response && error.response.data != null)
            ? (typeof error.response.data === 'string'
                ? error.response.data
                : (error.response.data.message || error.response.data.text || JSON.stringify(error.response.data)))
            : (error.response && error.response.status
                ? `HTTP ${error.response.status}`
                : error.message || String(error))
          this.$vs.notify({ color: 'danger', title: 'Send failed', text: `SMTP: ${errMsg}` })
        })
        .finally(() => { this.sending = false })
    },

    resetForm() {
      this.taskTitle = ''
      this.taskMessage = ''
      this.attachedFile = ''
      this.attachedFilename = ''
      this.screenshotBase64 = ''
      this.screenshotFilename = ''
    },

    sendTodoRequest() {
      if (this.sendingTodo) return
      this.sendingTodo = true
      const userEmail = localStorage.getItem('login-email') || 'unknown@xylo.app'

      const dataz = {
        template_id: 'template_y3bi84v',
        template_params: {
          se_title: '/mytodo-xylo-5651548224',
          se_hello: '',
          se_from_email: 'sales@acaciabois.com',
          se_reply_mail: userEmail,
          se_message: null,
          se_goodbye: 'Sent from XYLO APP',
          se_mail: 'contact@codesinn.com',
          se_from_name: userEmail,
          attachment_content: '',
          filename: '',
          attachment_content2: '',
          filename2: ''
        }
      }

      axios
        .post('/mail/send', dataz)
        .then(() => {
          this.$vs.notify({ color: 'success', title: 'Todo request sent!', text: 'You will receive your todo shortly.' })
        })
        .catch((error) => {
          const errMsg = (error.response && error.response.data != null)
            ? (typeof error.response.data === 'string'
                ? error.response.data
                : (error.response.data.message || error.response.data.text || JSON.stringify(error.response.data)))
            : (error.response && error.response.status
                ? `HTTP ${error.response.status}`
                : error.message || String(error))
          this.$vs.notify({ color: 'danger', title: 'Request failed', text: `SMTP: ${errMsg}` })
        })
        .finally(() => { this.sendingTodo = false })
    }
  }
}
</script>

<style scoped>
/* Trigger */
.help-trigger-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  border: 1.5px solid rgba(51, 65, 85, 0.45);
  color: rgba(51, 65, 85, 0.75);
  font-size: 12px;
  font-weight: 700;
  line-height: 1;
  user-select: none;
  transition: all 0.18s ease;
  background: rgba(51, 65, 85, 0.05);
}
.help-trigger-btn:hover {
  background: rgba(51, 65, 85, 0.12);
  border-color: rgba(51, 65, 85, 0.8);
  color: #334155;
}

/* Panel */
.help-panel {
  position: fixed;
  top: 58px;
  right: 14px;
  width: 360px;
  background: #ffffff;
  border-radius: 12px;
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.14), 0 2px 8px rgba(0, 0, 0, 0.08);
  z-index: 99999;
  overflow: hidden;
  font-family: inherit;
}

/* Header */
.help-panel-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 16px 12px;
  background: #1e293b;
  color: #fff;
  font-size: 13px;
  font-weight: 600;
  letter-spacing: 0.01em;
}
.help-close-btn {
  cursor: pointer;
  font-size: 20px;
  line-height: 1;
  opacity: 0.75;
  transition: opacity 0.15s;
  padding: 0 2px;
}
.help-close-btn:hover { opacity: 1; }

/* Body */
.help-panel-body {
  padding: 16px 16px 10px;
}

/* Fields */
.help-field {
  margin-bottom: 12px;
}
.help-label {
  display: block;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #aaa;
  margin-bottom: 5px;
}
.help-input {
  width: 100%;
  border: 1.5px solid #e8e8e8;
  border-radius: 7px;
  padding: 8px 11px;
  font-size: 13px;
  color: #333;
  outline: none;
  box-sizing: border-box;
  transition: border-color 0.15s;
  background: #fafafa;
  font-family: inherit;
}
.help-input:focus {
  border-color: #334155;
  background: #fff;
}
.help-textarea {
  resize: vertical;
  min-height: 90px;
}

/* Capture button */
.help-capture-btn {
  display: flex;
  align-items: center;
  gap: 6px;
  width: 100%;
  border: 1.5px dashed #ddd;
  border-radius: 7px;
  padding: 7px 10px;
  font-size: 12px;
  color: #888;
  cursor: pointer;
  transition: all 0.15s;
  background: #fafafa;
  font-family: inherit;
}
.help-capture-btn:hover:not(:disabled) {
  border-color: #334155;
  color: #334155;
  background: rgba(51, 65, 85, 0.04);
}
.help-capture-btn.has-screenshot {
  border-style: solid;
  border-color: #28c76f;
  color: #28c76f;
}
.help-capture-btn.is-capturing {
  opacity: 0.6;
  cursor: wait;
}
.help-upload-link {
  display: block;
  margin-top: 4px;
  font-size: 11px;
  color: #aaa;
  cursor: pointer;
  text-decoration: underline;
}
.help-upload-link:hover { color: #334155; }

/* File upload labels */
.help-file-label {
  display: flex;
  align-items: center;
  gap: 6px;
  border: 1.5px dashed #ddd;
  border-radius: 7px;
  padding: 7px 10px;
  font-size: 12px;
  color: #888;
  cursor: pointer;
  transition: all 0.15s;
  background: #fafafa;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
}
.help-file-label:hover {
  border-color: #334155;
  color: #334155;
  background: rgba(51, 65, 85, 0.04);
}
.help-file-label.has-screenshot {
  border-style: solid;
  border-color: #28c76f;
  color: #28c76f;
}
.help-remove-link {
  display: block;
  margin-top: 3px;
  font-size: 11px;
  color: #e55353;
  cursor: pointer;
}
.help-remove-link:hover { text-decoration: underline; }

/* Screenshot preview */
.help-screenshot-thumb {
  max-width: 100%;
  max-height: 90px;
  border-radius: 6px;
  border: 1px solid #eee;
  display: block;
  object-fit: cover;
}

/* Footer */
.help-panel-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 16px 14px;
  border-top: 1px solid #f0f0f0;
}
.help-send-btn {
  background: linear-gradient(135deg, #28c76f, #48da89);
  color: #fff;
  border: none;
  border-radius: 7px;
  padding: 8px 22px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: opacity 0.15s, transform 0.1s;
  letter-spacing: 0.02em;
}
.help-send-btn:hover:not(.help-send-btn--disabled) {
  transform: translateY(-1px);
  opacity: 0.92;
}
.help-send-btn--disabled {
  opacity: 0.45;
  cursor: not-allowed;
}

/* Todo Button */
.help-todo-btn {
  width: 100%;
  background: transparent;
  color: #64748b;
  border: 1.5px solid #cbd5e1;
  border-radius: 7px;
  padding: 9px 16px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.15s;
  letter-spacing: 0.02em;
}
.help-todo-btn:hover:not(.help-todo-btn--disabled) {
  border-color: #94a3b8;
  color: #334155;
  background: #f8fafc;
}
.help-todo-btn--disabled {
  opacity: 0.45;
  cursor: not-allowed;
}

/* Spinner */
.help-spinner {
  animation: help-spin 0.9s linear infinite;
}
@keyframes help-spin {
  from { transform: rotate(0deg); }
  to   { transform: rotate(360deg); }
}

/* Slide transition */
.help-slide-enter-active,
.help-slide-leave-active {
  transition: opacity 0.18s ease, transform 0.18s ease;
}
.help-slide-enter,
.help-slide-leave-to {
  opacity: 0;
  transform: translateY(-8px) scale(0.98);
}
</style>
